import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Tag, Select, Space, Typography, Card,
  Avatar, Tooltip, Tabs, Input,
} from 'antd';
import {
  AuditOutlined, UserOutlined, SearchOutlined,
  LoginOutlined, MailOutlined, TeamOutlined,
  DeleteOutlined, SettingOutlined, ImportOutlined,
} from '@ant-design/icons';
import { theme as antdTheme } from 'antd';
import axios from 'axios';
import AdminDateFilter, { resolveRange } from '../../components/AdminDateFilter';

const { Title, Text } = Typography;
const { Option } = Select;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ── Admin action config ────────────────────────────────────────────────────
const ACTION_COLOR = {
  suspend_org:        'error',
  unsuspend_org:      'success',
  override_plan:      'gold',
  delete_org:         'error',
  reset_counter:      'blue',
  add_suppression:    'orange',
  remove_suppression: 'green',
  update_send_rate:   'purple',
};
const ACTION_LABEL = {
  suspend_org:        'Suspend Org',
  unsuspend_org:      'Unsuspend Org',
  override_plan:      'Override Plan',
  delete_org:         'Delete Org',
  reset_counter:      'Reset Counter',
  add_suppression:    'Add Suppression',
  remove_suppression: 'Remove Suppression',
  update_send_rate:   'Update Send Rate',
};

// ── User activity config ───────────────────────────────────────────────────
const USER_ACTION_LABEL = {
  login:                'Signed in',
  password_changed:     'Password changed',
  profile_updated:      'Profile updated',
  campaign_created:     'Campaign created',
  campaign_updated:     'Campaign updated',
  campaign_duplicated:  'Campaign duplicated',
  campaign_deleted:     'Campaign deleted',
  campaign_sent:        'Campaign sent',
  campaign_scheduled:   'Campaign scheduled',
  campaign_test_sent:   'Test email sent',
  list_created:         'List created',
  list_updated:         'List updated',
  list_deleted:         'List deleted',
  subscriber_added:     'Subscriber added',
  subscriber_updated:   'Subscriber updated',
  subscriber_deleted:   'Subscriber deleted',
  subscribers_imported: 'Subscribers imported',
  subscribers_exported: 'Subscribers exported',
  template_created:     'Template created',
  template_updated:     'Template updated',
  template_deleted:     'Template deleted',
  ses_settings_saved:   'SES credentials saved',
  ses_tested:           'SES connection tested',
};

const USER_ACTION_COLOR = {
  login:                'blue',
  password_changed:     'orange',
  profile_updated:      'cyan',
  campaign_created:     'geekblue',
  campaign_updated:     'geekblue',
  campaign_duplicated:  'geekblue',
  campaign_deleted:     'red',
  campaign_sent:        'green',
  campaign_scheduled:   'lime',
  campaign_test_sent:   'purple',
  list_created:         'cyan',
  list_updated:         'cyan',
  list_deleted:         'red',
  subscriber_added:     'teal',
  subscriber_deleted:   'red',
  subscribers_imported: 'green',
  subscribers_exported: 'gold',
  template_created:     'purple',
  template_updated:     'purple',
  template_deleted:     'red',
  ses_settings_saved:   'orange',
  ses_tested:           'orange',
};

const USER_ACTION_ICON = {
  login:                <LoginOutlined />,
  password_changed:     <SettingOutlined />,
  profile_updated:      <UserOutlined />,
  campaign_created:     <MailOutlined />,
  campaign_updated:     <MailOutlined />,
  campaign_duplicated:  <MailOutlined />,
  campaign_deleted:     <DeleteOutlined />,
  campaign_sent:        <MailOutlined />,
  campaign_scheduled:   <MailOutlined />,
  campaign_test_sent:   <MailOutlined />,
  list_created:         <TeamOutlined />,
  list_updated:         <TeamOutlined />,
  list_deleted:         <DeleteOutlined />,
  subscriber_added:     <UserOutlined />,
  subscriber_deleted:   <DeleteOutlined />,
  subscribers_imported: <ImportOutlined />,
  subscribers_exported: <ImportOutlined />,
  ses_settings_saved:   <SettingOutlined />,
  ses_tested:           <SettingOutlined />,
};

const fmtDate = d => new Date(d).toLocaleString('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
});

// ── Admin Actions tab ──────────────────────────────────────────────────────
const AdminActionsTab = () => {
  const { token: tk } = antdTheme.useToken();

  const [data, setData]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [page, setPage]               = useState(1);
  const [actionF, setActionF]         = useState('');
  const [preset, setPreset]           = useState('All');
  const [customRange, setCustomRange] = useState(null);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { from, to } = resolveRange(preset, customRange);
      const params = { page: p, limit: 50 };
      if (actionF) params.action = actionF;
      if (from)    params.from   = from;
      if (to)      params.to     = to;
      const res = await axios.get('/api/admin/audit', { headers: authHeader(), params });
      setData(res.data.data);
      setTotal(res.data.total);
      setPage(p);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [actionF, preset, customRange]);

  useEffect(() => { load(1); }, [load]);

  const parseMeta = (meta) => {
    if (!meta) return {};
    try { return JSON.parse(meta); } catch { return {}; }
  };

  const columns = [
    {
      title: 'Admin', key: 'admin', width: 200,
      render: (_, r) => (
        <Space>
          <Avatar size={28} icon={<UserOutlined />} style={{ background: '#dc2626', fontSize: 12 }} />
          <Text strong style={{ fontSize: 12 }}>{r.admin_email || `Admin #${r.admin_id}`}</Text>
        </Space>
      ),
    },
    {
      title: 'Action', dataIndex: 'action', key: 'action', width: 160,
      render: a => <Tag color={ACTION_COLOR[a] || 'default'}>{ACTION_LABEL[a] || a.replace(/_/g, ' ')}</Tag>,
    },
    {
      title: 'Target', key: 'target',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          {r.target_name && <Text style={{ fontSize: 13 }}>{r.target_name}</Text>}
          {r.target_type && <Text type="secondary" style={{ fontSize: 11, textTransform: 'capitalize' }}>{r.target_type} #{r.target_id}</Text>}
        </Space>
      ),
    },
    {
      title: 'Details', key: 'meta',
      render: (_, r) => {
        const meta = parseMeta(r.meta);
        const entries = Object.entries(meta).filter(([, v]) => v !== null && v !== undefined);
        if (!entries.length) return <Text type="secondary">—</Text>;
        return (
          <Space size={4} wrap>
            {entries.map(([k, v]) => (
              <Tooltip key={k} title={k}>
                <Tag style={{ fontSize: 11, margin: 0 }}>{k}: <strong>{String(v)}</strong></Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Time', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: d => <Text type="secondary" style={{ fontSize: 12 }}>{fmtDate(d)}</Text>,
    },
  ];

  return (
    <>
      <AdminDateFilter preset={preset} setPreset={setPreset} customRange={customRange}
        setCustomRange={setCustomRange} onRefresh={() => load(1)} loading={loading} />

      <Card style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }} styles={{ body: { padding: 0 } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '14px 20px', borderBottom: `1px solid ${tk.colorBorderSecondary}` }}>
          <Select value={actionF || 'all'} onChange={v => setActionF(v === 'all' ? '' : v)} style={{ width: 200 }}>
            <Option value="all">All Actions</Option>
            {Object.entries(ACTION_LABEL).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
          </Select>
          <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>{total.toLocaleString()} entries</Text>
        </div>

        {total === 0 && !loading ? (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <AuditOutlined style={{ fontSize: 36, color: tk.colorTextQuaternary, display: 'block', marginBottom: 10 }} />
            <Text type="secondary">No admin actions recorded yet.</Text>
          </div>
        ) : (
          <Table rowKey="id" dataSource={data} columns={columns} loading={loading} size="small"
            scroll={{ x: 'max-content' }}
            pagination={{ current: page, pageSize: 50, total, onChange: p => load(p), showTotal: n => `${n} entries`, style: { padding: '12px 20px', margin: 0 } }}
            style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
          />
        )}
      </Card>
    </>
  );
};

// ── User Activity tab ──────────────────────────────────────────────────────
const UserActivityTab = () => {
  const { token: tk } = antdTheme.useToken();

  const [data, setData]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [page, setPage]               = useState(1);
  const [actionF, setActionF]         = useState('');
  const [orgFilter, setOrgF]          = useState('');
  const [search, setSearch]           = useState('');
  const [orgs, setOrgs]               = useState([]);
  const [preset, setPreset]           = useState('All');
  const [customRange, setCustomRange] = useState(null);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { from, to } = resolveRange(preset, customRange);
      const params = { page: p, limit: 50 };
      if (actionF)   params.action = actionF;
      if (orgFilter) params.org_id = orgFilter;
      if (from)      params.from   = from;
      if (to)        params.to     = to;
      const res = await axios.get('/api/admin/user-activity', { headers: authHeader(), params });
      let rows = res.data.data;
      // Client-side email/name search
      if (search.trim()) {
        const q = search.toLowerCase();
        rows = rows.filter(r => (r.user_email || '').toLowerCase().includes(q) || (r.entity_name || '').toLowerCase().includes(q));
      }
      setData(rows);
      setTotal(res.data.total);
      setPage(p);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [actionF, orgFilter, search, preset, customRange]);

  useEffect(() => { load(1); }, [load]);

  useEffect(() => {
    axios.get('/api/admin/orgs', { headers: authHeader() }).then(r => setOrgs(r.data)).catch(() => {});
  }, []);

  const parseMeta = (meta) => {
    if (!meta) return {};
    try { return JSON.parse(meta); } catch { return {}; }
  };

  const columns = [
    {
      title: 'User', key: 'user', width: 220,
      render: (_, r) => (
        <Space>
          <Avatar size={28} icon={<UserOutlined />} style={{ background: 'var(--brand-primary)', fontSize: 12 }} />
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 12 }}>{r.user_email || `User #${r.user_id}`}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{r.org_name}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Action', dataIndex: 'action', key: 'action', width: 180,
      render: a => (
        <Space size={6}>
          <span style={{ fontSize: 14 }}>{USER_ACTION_ICON[a] || <AuditOutlined />}</span>
          <Tag color={USER_ACTION_COLOR[a] || 'default'} style={{ margin: 0 }}>
            {USER_ACTION_LABEL[a] || a.replace(/_/g, ' ')}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Target', key: 'target',
      render: (_, r) => r.entity_name ? (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>{r.entity_name}</Text>
          {r.entity_type && <Text type="secondary" style={{ fontSize: 11, textTransform: 'capitalize' }}>{r.entity_type}{r.entity_id ? ` #${r.entity_id}` : ''}</Text>}
        </Space>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Details', key: 'meta',
      render: (_, r) => {
        const meta = parseMeta(r.meta);
        const entries = Object.entries(meta).filter(([, v]) => v !== null && v !== undefined && !Array.isArray(v));
        if (!entries.length) return <Text type="secondary">—</Text>;
        return (
          <Space size={4} wrap>
            {entries.slice(0, 3).map(([k, v]) => (
              <Tooltip key={k} title={k}>
                <Tag style={{ fontSize: 11, margin: 0 }}>{k}: <strong>{String(v)}</strong></Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'IP', dataIndex: 'ip', key: 'ip', width: 130,
      render: v => <Text type="secondary" style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>{v || '—'}</Text>,
    },
    {
      title: 'Time', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: d => <Text type="secondary" style={{ fontSize: 12 }}>{fmtDate(d)}</Text>,
    },
  ];

  return (
    <>
      <AdminDateFilter preset={preset} setPreset={setPreset} customRange={customRange}
        setCustomRange={setCustomRange} onRefresh={() => load(1)} loading={loading} />

      <Card style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }} styles={{ body: { padding: 0 } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '14px 20px', borderBottom: `1px solid ${tk.colorBorderSecondary}` }}>
          <Input
            placeholder="Search user email or entity…"
            prefix={<SearchOutlined style={{ color: tk.colorTextQuaternary }} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ width: 240, borderRadius: 8 }}
          />
          <Select value={actionF || 'all'} onChange={v => setActionF(v === 'all' ? '' : v)} style={{ width: 190 }}>
            <Option value="all">All Actions</Option>
            {Object.entries(USER_ACTION_LABEL).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
          </Select>
          <Select value={orgFilter || 'all'} onChange={v => setOrgF(v === 'all' ? '' : v)} style={{ width: 190 }} showSearch optionFilterProp="children">
            <Option value="all">All Organisations</Option>
            {orgs.map(o => <Option key={o.id} value={String(o.id)}>{o.name}</Option>)}
          </Select>
          <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>{total.toLocaleString()} events</Text>
        </div>

        {total === 0 && !loading ? (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <UserOutlined style={{ fontSize: 36, color: tk.colorTextQuaternary, display: 'block', marginBottom: 10 }} />
            <Text type="secondary">No user activity recorded yet.</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>Activity is recorded as users log in and interact with the platform.</Text>
          </div>
        ) : (
          <Table rowKey="id" dataSource={data} columns={columns} loading={loading} size="small"
            pagination={{ current: page, pageSize: 50, total, onChange: p => load(p), showTotal: n => `${n} events`, style: { padding: '12px 20px', margin: 0 } }}
            style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
          scroll={{ x: 'max-content' }}
          />
        )}
      </Card>
    </>
  );
};

// ── Page shell ─────────────────────────────────────────────────────────────
const AdminAuditLog = () => (
  <div style={{ padding: '0 2px' }}>
    <div style={{ marginBottom: 20 }}>
      <Title level={4} style={{ marginBottom: 4 }}>Activity Logs</Title>
      <Text type="secondary">Platform-wide audit trail — admin actions and user activity.</Text>
    </div>

    <Tabs
      defaultActiveKey="admin"
      items={[
        {
          key:      'admin',
          label:    <Space><AuditOutlined />Admin Actions</Space>,
          children: <AdminActionsTab />,
        },
        {
          key:      'users',
          label:    <Space><UserOutlined />User Activity</Space>,
          children: <UserActivityTab />,
        },
      ]}
    />
  </div>
);

export default AdminAuditLog;
